import React from 'react';
import {
  Box, HStack, Pressable, Text, VStack,
} from '@gluestack-ui/themed';

import type { OralExamQuestion } from '@/common/api/models';
import { OralExamQuestionText } from '@/common/components/OralExamQuestionText';
import { conceptRepeatsTopic, questionTitleRepeatsTopic } from '../utils/questionTitleRepeatsTopic';

type Props = {
  alternatives: OralExamQuestion[];
  topicLabel?: string;
  selectedReplacementId?: string;
  onSelectReplacement: (questionId: string) => void;
};

export function SwapAlternativeCards({
  alternatives,
  topicLabel,
  selectedReplacementId,
  onSelectReplacement,
}: Props) {
  return (
    <VStack space="sm">
      {alternatives.map((alt) => {
        const altId = alt.questionId ?? '';
        const selected = selectedReplacementId === altId;
        return (
          <Pressable
            key={altId}
            accessibilityRole="radio"
            accessibilityState={{ selected }}
            testID={`scheduled-swap-alt-${altId}`}
            onPress={() => onSelectReplacement(altId)}
            borderWidth={selected ? 1.5 : 1}
            borderColor={selected ? '$brandBase' : '$borderLight'}
            bg={selected ? '$bgBrandSubtle' : '$bgCard'}
            borderRadius="$md"
            px="$3"
            py="$2.5"
            $hover={{ bg: '$bgHover' }}
            sx={{
              _web: {
                ':focus-visible': {
                  outlineWidth: 2,
                  outlineColor: '$borderFocus',
                  outlineStyle: 'solid',
                },
              },
            }}
          >
            <HStack space="sm" alignItems="flex-start">
              <Box
                mt="$1"
                w="$3.5"
                h="$3.5"
                borderRadius="$full"
                borderWidth={1.5}
                borderColor={selected ? '$brandBase' : '$borderMedium'}
                bg={selected ? '$brandBase' : 'transparent'}
              />
              <VStack flex={1} space="xs">
                {!!alt.concept && !conceptRepeatsTopic(alt.concept, topicLabel) && (
                  <Text size="xs" color="$textHint" testID={`scheduled-swap-alt-concept-${altId}`}>
                    {alt.concept}
                  </Text>
                )}
                <OralExamQuestionText
                  size="sm"
                  question={alt.question ?? ''}
                  fillKind={alt.fillKind}
                  fills={alt.fills}
                  showFillSlot
                  hideTitle={questionTitleRepeatsTopic(alt.question, alt.concept)}
                />
              </VStack>
            </HStack>
          </Pressable>
        );
      })}
    </VStack>
  );
}
